/**
 * @(#)2019/6/17.
 * Number，Input组件公共部分
 * @author 飞光
 *
 * Copyright (c) 2019, YUNXI. All rights reserved.
 * YUNXI PROPRIETARY/CONFIDENTIAL. Use is subject to license terms.
 */

import MultipleInput from "../MultipleInput";
import React, { Component } from "react";
import { nodeFactory, cloneDeep, yxStyleProps } from "../../utils";
import { Input, Form, Tooltip } from "antd";
import Config from "../../config";
import { get, isObject, isArray, merge, set } from "lodash";
import defaultRules from "./defaultRules";

const FormItem = Form.Item;

/*
 * -----------------------公共部分参数--------------------------------------------------
 * @param form {object} 必须从页面中把this.props.form传递进来
 * @param children {ReactNode} 子节点，需要展示的内容，默认为input
 * @param isControlled {boole} 是否为受控组件，默认false，非受控组件会引入form.item，
 *         受控组件指的是，值由state控制，非受控组件则由用户输入控制，在输入完毕后才获取值
 * @param id {String} required 该控件的ID，用于在非受控组件时，进行值的获取,必填项，默认为随机数
 * @param change {event} 内容回调函数
 * @param defaultValue {string} 默认值
 * @param label {string} 描述内容
 * @param beforeNode {reactNode | string} input之前的内容
 * @param afterNode {reactNode | string} input之后的内容
 * @param labelLength {number} 决定裁掉多少个label文字
 * @param mainClassName {string} input外层div的className
 * 
 *
 *         rules的所有信息，既可以接受简单参数，也可以接受和antd一致的对象参数
 *         注意 rules只能接受对象参数，不建议数组格式，因为数组格式无法补全
 *         如：min:12  或   min:{min:10,message:"不能小于10"}, 参数表只列出了经过二次封装的校验项，不在参数表内的只能接受object格式且和antd保持一致
 * @param rules.required {boole/object} 是否为必填项，默认false
 * @param rules.max {number/object} 最大字符长度
 * @param rules.min {number/object} 最小字符长度
 * @param rules.len {number/object} 所需字符长度，（max+min）
 * 自定义校验这么写   validator:{
                  validator: async (r,v,c)=>{
                    // c("哈哈哈")
                    if(v==12){
                      c("haha")
                    }
                  }
                }
 * ---其他默认校验项参考defaultRules.js文件
 * 
 * @param inputParam {object} 直接给input控件本身设置的参数 如  htmlType  参考antd
 * @param getFieldDecoratorParam {object} 直接给input的getFieldDecorator设置的参数 参考antd
 * @param formItemParam {object} 给formItem的参数，和antd官 mei网参数保持一致  参考antd
 * 
 * */
class Basics {
  static noControlled = {};
  static basicsProps = {
    mainClassName: "yx_yxInput", //input外层div的className
    label: "", //描述lable
    children: "", //子节点
    isControlled: false, //是否为受控组件
    id: "",
    disabled: false, //是否禁用
    defaultValue: undefined, // 默认值
    value: undefined, //控件的值
    change: null, //内容回调函数
    placeholder: "", //默认提示
    beforeNode: "", //input之前的内容，
    afterNode: "", //input之后的内容，
    //校验规则
    rules: {
      required: false,
    },
    inputParam: {}, //直接给input控件本身设置的参数 如  htmlType
    //直接给input的getFieldDecorator设置的参数
    getFieldDecoratorParam: {
      validateFirst: true, //当某一规则校验不通过时，是否停止剩下的规则的校验
    },
    //给formItem的参数，和antd官网参数保持一致
    formItemParam: {
      className: "",
      label: "",
      // colon:false,
      ...Config.formItemLayout,
    },
  };
  //判断formItem是否固定高度,高度类型
  static classNameHeight(formItemParam, tmpProps) {
    let className = formItemParam.className + " yx_yxFormitem_horizontal";
    let itemFormClass = " yx_yxFormitem_size_default"; //高度类型
    let inputClassType = {
      checkbox: ["Checkbox", "Radio"], //单选复选，开关
      elastic: ["KindEditor", "TextArea", "Upload", "Select"], //弹性
    };
    for (let i in inputClassType) {
      if (inputClassType[i].includes(get(tmpProps, "inputType", ""))) {
        itemFormClass = ` yx_yxFormitem_size_${i}`;
      }
    }
    className += itemFormClass;
    return className;
  }
  //生成校验规则
  static rules(tmpProps) {
    let rulesArr = [];
    let propsRules = get(tmpProps, "rules", {}); //外部rules
    let basicsPropsRules = { ...this.basicsProps.rules };
    const maxDefault = ["String", "Phone", "EMail", "Search", "TextArea"]; //设置默认长度类型
    if (maxDefault.includes(get(tmpProps, "inputType", ""))) {
      basicsPropsRules.max = get(Config, "input.defaultInputMax", 50); //设置默认max
    } else {
      delete propsRules.max;
    }

    let rulesParamer = merge({}, basicsPropsRules, propsRules);
    // 默认校验
    let rulesList = (item) => {
      return defaultRules(tmpProps, item);
    };
    if (isArray(rulesParamer)) {
      rulesArr = rulesParamer;
    }
    if (isObject(rulesParamer)) {
      for (let i in rulesParamer) {
        //如果该项不等于对象，那么就设置默认类型校验
        if (typeof rulesParamer[i] !== "object") {
          rulesParamer[i] ? rulesArr.push(rulesList(rulesParamer)[i]) : "";
        } else {
          //如果传的是对象，并且格式需要和antd的api一致
          let ruleItem = merge(rulesList(rulesParamer[i])[i], rulesParamer[i]);
          rulesArr.push(ruleItem);
        }
      }
    }
    return rulesArr;
  }

  //根据不同的类型返回不同的取值方式
  static inputChangeTypeVal(e, dateString, type) {
    let val = "";
    //如果是时间格式，直接返回时间
    if (dateString && typeof dateString == "string") return dateString;
    //否则返回其他格式
    if (e && e.target) {
      if (e.target.type == "checkbox") {
        val = get(e.target, "checked", false);
      } else {
        val = e.target.value;
      }
    } else {
      if (type == "Select") {
        val = get(dateString, "props");
      } else {
        val = e;
      }
    }
    return val;
  }

  //生产html
  static html(tmpProps) {
    try {
      let THIS = this;
      //合并默认参数和外部参数
      tmpProps = Object.assign({}, THIS.basicsProps, yxStyleProps(tmpProps));

      //设置label可以在外围参数设置，不在form上设置
      tmpProps.formItemParam.label = tmpProps.label;
      //设置disabled可以在外围参数设置，不在input上设置
      tmpProps.inputParam.disabled = tmpProps.disabled;
      //设置placeholder可以在外围设置
      tmpProps.inputParam.placeholder = tmpProps.placeholder;
      // 设置formItem 副标题extra
      tmpProps.formItemParam.extra = tmpProps.extra;
      tmpProps.className = [
        tmpProps.className,
        tmpProps.inputParam.className,
      ].join(" ");
      //合并formItem部分参数
      let formItemParam = Object.assign(
        {},
        this.basicsProps.formItemParam,
        tmpProps.formItemParam
      );
      formItemParam.className = THIS.classNameHeight(formItemParam, tmpProps); //给formItem附加className
      let parame = {};
      let inputHtml = "";
      const children = nodeFactory(get(tmpProps, "children", null), tmpProps);
      tmpProps.children = children;
      if (!tmpProps.isControlled) {
        //合并getFieldDecoratorParam部分参数
        let getFieldDecoratorParam = Object.assign(
          {},
          THIS.basicsProps.getFieldDecoratorParam,
          tmpProps.getFieldDecoratorParam
        );
        let getFieldDecorator = get(
          tmpProps,
          "form.getFieldDecorator",
          () => {}
        );
        //合并props参数
        if (tmpProps.children) {
          let cprops = Object.assign(
            {},
            tmpProps.children.props,
            tmpProps.inputParam
          );
          tmpProps.children = { ...tmpProps.children };
          tmpProps.children.props = { ...cprops };
        }
        //设置label超出隐藏
        formItemParam.label = (() => {
          let label = !get(tmpProps, "nolabel", false)
            ? formItemParam.label
            : "";
          let labelLength = get(
            tmpProps,
            "labelLength",
            get(Config, "dynamicForm.labelLength", 6)
          );
          if (label && label.length > labelLength) {
            label = (
              <Tooltip title={formItemParam.label}>
                {label.substr(0, labelLength - 1)}...
              </Tooltip>
            );
          }
          return label;
        })();

        inputHtml = (
          <FormItem {...formItemParam}>
            {/*处理input的前缀后缀问题*/}
            <div
              className={
                tmpProps.beforeNode ||
                nodeFactory(
                  tmpProps.afterNode,
                  get(this.noControlled, `${tmpProps.id}`)
                )
                  ? "yx_yxInput_inputAdditionalNode"
                  : ""
              }
            >
              {tmpProps.beforeNode ? (
                <div className="yx_yxInput_beforeNode">
                  {nodeFactory(
                    tmpProps.beforeNode,
                    get(this.noControlled, `${tmpProps.id}`)
                  )}
                </div>
              ) : (
                ""
              )}
              {getFieldDecorator(tmpProps.id, {
                rules: THIS.rules(tmpProps),
                initialValue: tmpProps.defaultValue,
                onChange: (e, dateString) => {
                  if (tmpProps.change) {
                    set(
                      this.noControlled,
                      `${tmpProps.id}`,
                      THIS.inputChangeTypeVal(e, dateString, tmpProps.inputType)
                    );
                    tmpProps.change(
                      THIS.inputChangeTypeVal(e, dateString),
                      tmpProps.id,
                      e,
                      dateString
                    );
                  }
                },
                ...getFieldDecoratorParam,
              })(tmpProps.children || <Input {...tmpProps.inputParam} />)}
              {tmpProps.afterNode ? (
                <div className="yx_yxInput_afterNode">
                  {nodeFactory(
                    tmpProps.afterNode,
                    get(this.noControlled, `${tmpProps.id}`)
                  )}
                </div>
              ) : (
                ""
              )}
            </div>
          </FormItem>
        );
      } else {
        parame = {
          onChange: tmpProps.change,
          defaultValue: tmpProps.defaultValue,
          value: tmpProps.value,
          ...tmpProps.inputParam,
        };
        //合并props参数
        if (tmpProps.children) {
          let cprops = Object.assign({}, tmpProps.children.props, parame);
          tmpProps.children = { ...tmpProps.children };
          tmpProps.children.props = { ...cprops };
        }
        inputHtml = (
          <FormItem {...formItemParam}>
            <div
              className={
                tmpProps.beforeNode || tmpProps.afterNode
                  ? "yx_yxInput_inputAdditionalNode"
                  : ""
              }
            >
              {tmpProps.beforeNode ? (
                <div>{nodeFactory(tmpProps.beforeNode, tmpProps.value)}</div>
              ) : (
                ""
              )}
              {tmpProps.children || <Input {...parame} />}
              {tmpProps.afterNode ? (
                <div>{nodeFactory(tmpProps.afterNode, tmpProps.value)}</div>
              ) : (
                ""
              )}
            </div>
          </FormItem>
        );
      }
      return <div className={tmpProps.mainClassName}>{inputHtml}</div>;
    } catch (e) {
      console.error(e);
    }
  }
}

export default Basics;



// WEBPACK FOOTER //
// ./src/yx-widget/component/YxInput/Basics.js